import axios from 'axios';
import config from '../../config';
import logout from '../../utils/auth/logout';

const api = axios.create({
    baseURL: config.API_SERVER
});

/**
 * REQUEST → adiciona access token
 */
api.interceptors.request.use((config) => {
    // Normaliza URLs para evitar duplicação do prefixo `/api/`.
    // Ex.: baseURL = "https://.../api/" + url = "api/v1/..." -> ".../api/api/v1/..."
    // Aqui transformamos "api/v1/..." (ou "/api/v1/...") em "v1/...".
    const baseURL = String(config.baseURL ?? '');
    const url = config.url;
    const baseEndsWithApi = /\/api\/?$/.test(baseURL.replace(/\/+$/, ''));

    if (baseEndsWithApi && typeof url === 'string' && (url.startsWith('api/') || url.startsWith('/api/'))) {
        config.url = url.replace(/^\/?api\//, '');
    }

    const token = localStorage.getItem('access_token');
    // console.log(`interceptors - access_token:${token}`);

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) prom.reject(error);
        else prom.resolve(token);
    });
    failedQueue = [];
};

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // ⚠️ Se não for 401, sai
        if (error.response?.status !== 401) {
            return Promise.reject(error);
        }

        // ⚠️ Evita loop - se a requisição já foi reenviada com um token novo
        // e ainda assim voltou 401, não adianta tentar de novo
        if (originalRequest._retry) {
            logout();
            return Promise.reject(error);
        }

        // ⚠️ Nunca tenta refresh se o erro veio do próprio refresh
        if (originalRequest.url.includes('token/refresh')) {
            logout();
            return Promise.reject(error);
        }

        const refresh = localStorage.getItem('refresh_token');

        // ⚠️ Sem refresh → logout
        if (!refresh) {
            logout();
            return Promise.reject(error);
        }

        originalRequest._retry = true;

        // Telas com polling (ex.: notificações) fazem várias chamadas em
        // paralelo - quando o access token expira, todas recebem 401 quase
        // ao mesmo tempo. Sem essa fila, cada uma dispararia sua própria
        // chamada de refresh concorrente; aqui, só a primeira chamada
        // refresh de verdade, as demais esperam o resultado dela e reusam
        // o token novo.
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then((newAccess) => {
                originalRequest.headers.Authorization = `Bearer ${newAccess}`;
                return api(originalRequest);
            });
        }

        isRefreshing = true;

        try {
            const res = await axios.post(`${config.API_SERVER}token/refresh/`, { refresh });

            const newAccess = res.data.access;
            // Com ROTATE_REFRESH_TOKENS ativo no backend, cada refresh vem
            // acompanhado de um refresh token novo - precisa ser salvo,
            // senão a sessão continua com validade fixa em vez de
            // "deslizante" (renovando enquanto o usuário estiver ativo).
            const newRefresh = res.data.refresh;

            localStorage.setItem('access_token', newAccess);
            if (newRefresh) {
                localStorage.setItem('refresh_token', newRefresh);
            }

            api.defaults.headers.common.Authorization = `Bearer ${newAccess}`;
            api.defaults.headers.Authorization = `Bearer ${newAccess}`;
            originalRequest.headers.Authorization = `Bearer ${newAccess}`;

            processQueue(null, newAccess);

            return api(originalRequest);
        } catch (err) {
            processQueue(err, null);
            logout();
            return Promise.reject(err);
        } finally {
            isRefreshing = false;
        }
    }
);

export default api;
